<!-- DemandNoticeModal.vue - With Email and Phone Functionality -->
<template>
  <div class="modal fade" id="demandNotice" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-labelledby="staticBackdropLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="staticBackdropLabel">Demand Notice Preview</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <!-- Show email form if emailMode is active -->
          <div v-if="emailMode" class="row">
            <div class="col-12">
              <div class="card border-primary">
                <div class="card-header bg-primary text-white">
                  <h6 class="mb-0 text-white">
                    <i class="mdi mdi-email-outline me-2"></i>
                    Send Demand Notice via Email & SMS
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <h6><strong>Property Details:</strong></h6>
                      <p class="mb-1"><strong>LR Number:</strong> {{property.LRNumber}}</p>
                      <p class="mb-1"><strong>Owner:</strong> {{property.ownerNames}}</p>
                      <p class="mb-1"><strong>Current Balance:</strong> <span class="text-danger">KES {{numberFormat(property.currentBalance || property.outstandingArrears || 0)}}</span></p>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group mb-3">
                        <label for="recipientEmail" class="form-label"><strong>Email Address <span class="text-danger">*</span></strong></label>
                        <input
                            v-model="emailForm.recipientEmail"
                            type="email"
                            class="form-control"
                            id="recipientEmail"
                            placeholder="Enter email address"
                            :class="{ 'is-invalid': emailError }"
                            required>
                        <div v-if="emailError" class="invalid-feedback">{{emailError}}</div>
                        <div class="form-text">
                          <small class="text-muted">
                            <i class="mdi mdi-information-outline me-1"></i>
                            Default: {{property.ownerEmail || 'No email on file'}}
                          </small>
                        </div>
                      </div>

                      <div class="form-group mb-3">
                        <label for="recipientPhone" class="form-label"><strong>Phone Number (Optional)</strong></label>
                        <input
                            v-model="emailForm.recipientPhone"
                            type="tel"
                            class="form-control"
                            id="recipientPhone"
                            placeholder="Enter phone number (e.g., 0712345678)"
                            maxlength="15">
                        <div class="form-text">
                          <small class="text-muted">
                            <i class="mdi mdi-information-outline me-1"></i>
                            Default: {{property.ownerPhoneNo || 'No phone on file'}} | SMS notification will be sent if provided
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Email Preview -->
                  <div class="alert alert-info">
                    <h6><i class="mdi mdi-email-newsletter-outline me-2"></i>Notification Preview:</h6>
                    <p class="mb-1"><strong>Email Subject:</strong> Demand Notice - Property {{property.LRNumber}} -  County</p>
                    <p class="mb-1"><strong>Email Content:</strong> Professional HTML demand notice with all property details, charges breakdown, and payment instructions.</p>
                    <p class="mb-1" v-if="emailForm.recipientPhone.trim()"><strong>SMS:</strong> Brief notification with amount due and link to full notice.</p>
                    <p class="mb-0"><strong>Amount Due:</strong> KES {{numberFormat(property.currentBalance || property.outstandingArrears || 0)}}</p>
                  </div>
                </div>
                <div class="card-footer">
                  <button @click="emailMode = false" type="button" class="btn btn-secondary me-2">
                    <i class="mdi mdi-arrow-left me-2"></i>Back to Preview
                  </button>
                  <button @click="sendEmailDemandNotice" type="button" class="btn btn-success" :disabled="sendingEmail">
                    <span v-if="sendingEmail">
                      <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Sending...
                    </span>
                    <span v-else>
                      <i class="mdi mdi-send me-2"></i>Send Notice
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Show email success message -->
          <div v-else-if="emailSent" class="row">
            <div class="col-12">
              <div class="text-center">
                <div class="mb-4">
                  <div class="avatar-lg mx-auto mb-4">
                    <div class="avatar-title rounded-circle bg-success text-white">
                      <i class="mdi mdi-check-all font-size-24"></i>
                    </div>
                  </div>
                  <h4 class="text-success">Notice Sent Successfully!</h4>
                  <p class="text-muted">
                    Demand notice has been sent to <strong>{{emailForm.recipientEmail}}</strong>
                  </p>
                  <p class="text-muted" v-if="emailForm.recipientPhone.trim()">
                    SMS notification also sent to <strong>{{emailForm.recipientPhone}}</strong>
                  </p>
                  <p class="text-muted">
                    Property: <strong>{{property.LRNumber}}</strong> |
                    Owner: <strong>{{property.ownerNames}}</strong> |
                    Amount: <strong class="text-danger">KES {{numberFormat(property.currentBalance || property.outstandingArrears || 0)}}</strong>
                  </p>
                </div>
                <div class="d-flex justify-content-center gap-2">
                  <button @click="resetEmailForm" type="button" class="btn btn-primary">
                    <i class="mdi mdi-email-plus me-2"></i>Send Another Notice
                  </button>
                  <button @click="$emit('print-demand-notice')" type="button" class="btn btn-success">
                    <i class="bx bx-printer me-2"></i>Print Notice
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Default preview mode -->
          <div v-else class="row">
            <div class="col-6">
              <address>
                <strong class="text-capitalize">Owner's name: {{property.ownerNames}}</strong><br>
                Phone No. {{maskPhoneNumber(property.ownerPhoneNo)}}<br>
                <span v-if="property.ownerEmail">Email: {{property.ownerEmail}}<br></span>
                <span v-if="property.percelAddress">Physical Address: {{property.percelAddress}}<br></span>
                <br>
                Date: {{formatDate(property.dateCreated)}}
              </address>
            </div>

            <div class="col-12">
              <div class="table-responsive text-uppercase">
                <table class="table table-nowrap table-bordered vertical-align-middle">
                  <thead class="table-light">
                  <tr>
                    <th colspan="3">Property Details</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr>
                    <td class="">
                      UPN No.: <strong>{{property.UPN || property.LRNumber}}</strong>
                    </td>
                    <td class="">
                      Block No.: <strong>{{property.block || 'N/A'}}</strong>
                    </td>
                    <td class="">
                      Plot/LR No.: <strong>{{property.LRNumber}}</strong>
                    </td>
                  </tr>

                  <tr>
                    <td class="">
                      Sub County: <strong>{{property.subCountyName}}</strong>
                    </td>
                    <td class="">
                      Ward: <strong>{{property.wardName}}</strong>
                    </td>
                    <td class="">
                      Zone/Market: <strong>{{property.zone || 'N/A'}}</strong>
                    </td>
                  </tr>

                  <tr v-if="property.percelSize">
                    <td class="">
                      Parcel Size: <strong>{{numberFormat(property.percelSize)}} Ha</strong>
                    </td>
                    <td class="">
                      Land Use: <strong>{{property.useName || 'Not Set'}}</strong>
                    </td>
                    <td class="">
                      Status: <strong>{{property.status || 'Active'}}</strong>
                    </td>
                  </tr>

                  <tr>
                    <td colspan="3" class="">
                      Rate Payer: <strong>{{property.ownerNames}}</strong>
                    </td>
                  </tr>

                  <tr>
                    <td colspan="3" class="">
                      Physical Address: <strong>{{property.percelAddress || 'N/A'}}</strong>
                    </td>
                  </tr>

                  <tr>
                    <td colspan="3"></td>
                  </tr>

                  <tr>
                    <th colspan="3" class="table-secondary">Details of Charges</th>
                  </tr>

                  <!-- Enhanced Financial Details matching the main table -->
                  <tr>
                    <th>Annual Land Rates</th>
                    <th colspan="2" class="text-success">KES {{numberFormat(property.annualLandRates || 0)}}</th>
                  </tr>

                  <tr>
                    <th>Annual Ground Rent</th>
                    <th colspan="2" class="text-info">KES {{numberFormat(property.annualGroundRent || 0)}}</th>
                  </tr>

                  <tr>
                    <th>Total Annual Charges</th>
                    <th colspan="2" class="text-primary">KES {{numberFormat((property.annualLandRates || 0) + (property.annualGroundRent || 0))}}</th>
                  </tr>

                  <tr class="table-warning">
                    <th>Land Rates Arrears</th>
                    <th colspan="2" class="text-warning">KES {{numberFormat(property.landRatesArrears || 0)}}</th>
                  </tr>

                  <tr class="table-warning">
                    <th>Ground Rent Arrears</th>
                    <th colspan="2" class="text-warning">KES {{numberFormat(property.groundRentArrears || 0)}}</th>
                  </tr>

                  <tr class="table-danger">
                    <th>Accumulated Penalty</th>
                    <th colspan="2" class="text-danger">KES {{numberFormat(property.accumPenalty || 0)}}</th>
                  </tr>

                  <tr class="table-danger">
                    <th>Accumulated Interest</th>
                    <th colspan="2" class="text-danger">KES {{numberFormat(property.accumInterest || 0)}}</th>
                  </tr>

                  <tr class="table-info">
                    <th>Total Payments Made</th>
                    <th colspan="2" class="text-info">KES {{numberFormat(property.payments || 0)}}</th>
                  </tr>

                  <tr class="table-dark">
                    <th>Current Outstanding Balance</th>
                    <th colspan="2" class="text-light fw-bold">KES {{numberFormat(property.currentBalance || property.outstandingArrears || 0)}}</th>
                  </tr>

                  <!-- Breakdown Summary -->
                  <tr>
                    <td colspan="3" class="text-center">
                      <small class="text-muted">
                        Balance Breakdown: Principal Arrears + Penalties + Interest - Payments = Current Balance
                      </small>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Payment Priority Information -->
            <div class="col-12 mb-3">
              <div class="alert alert-info">
                <h6><i class="mdi mdi-information me-2"></i>Payment Priority (Per BRD Guidelines):</h6>
                <ol class="mb-0">
                  <li><strong>Interest:</strong> KES {{numberFormat(property.accumInterest || 0)}}</li>
                  <li><strong>Penalty:</strong> KES {{numberFormat(property.accumPenalty || 0)}}</li>
                  <li><strong>Land Rates Principal:</strong> KES {{numberFormat(property.landRatesArrears || 0)}}</li>
                  <li><strong>Ground Rent Principal:</strong> KES {{numberFormat(property.groundRentArrears || 0)}}</li>
                </ol>
                <hr>
                <p class="mb-0"><strong>Total Amount Due:</strong> KES {{numberFormat(property.currentBalance || property.outstandingArrears || 0)}}</p>
              </div>
            </div>

            <div class="col-12">
              <button type="button" class="btn btn-secondary text-uppercase" data-bs-dismiss="modal">Close</button>
              <button @click="showEmailForm" type="button" class="btn btn-info text-uppercase me-2" :disabled="!property.ownerEmail && !canSendEmail">
                <i class="mdi mdi-email-outline me-2"></i>Send Notice
              </button>
              <button @click="$emit('print-demand-notice')" type="button" class="btn btn-primary text-uppercase">
                <i class="bx bx-printer font-size-16 align-middle me-2"></i>
                Print Notice
              </button>
            </div>
          </div>
        </div>
        <div class="modal-footer"></div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import {execute, landRate} from "@/api";

export default {
  name: 'DemandNoticeModal',
  props: {
    property: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['print-demand-notice'],
  data() {
    return {
      emailMode: false,
      emailSent: false,
      sendingEmail: false,
      emailError: '',
      emailForm: {
        recipientEmail: '',
        recipientPhone: ''
      }
    }
  },
  computed: {
    canSendEmail() {
      return this.emailForm.recipientEmail.trim() !== '' || this.property.ownerEmail;
    }
  },
  watch: {
    property: {
      handler(newProperty) {
        if (newProperty && newProperty.ownerEmail) {
          this.emailForm.recipientEmail = newProperty.ownerEmail;
        }
        if (newProperty && newProperty.ownerPhoneNo) {
          this.emailForm.recipientPhone = newProperty.ownerPhoneNo;
        }
      },
      immediate: true
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return new Date().toLocaleDateString('en-GB');

      let d = new Date(date);
      let day = d.getDate();
      let month = d.toLocaleString('en-US', { month: 'short' });
      let year = d.getFullYear();

      let suffix = '';
      if (day % 10 === 1 && day !== 11) {
        suffix = 'st';
      } else if (day % 10 === 2 && day !== 12) {
        suffix = 'nd';
      } else if (day % 10 === 3 && day !== 13) {
        suffix = 'rd';
      } else {
        suffix = 'th';
      }
      return `${day}${suffix} ${month} ${year}`;
    },

    numberFormat(num) {
      if (num === null || num === undefined || isNaN(num)) {
        return '0';
      }
      const britishNumberFormatter = new Intl.NumberFormat("en-GB");
      return britishNumberFormatter.format(parseFloat(num));
    },

    maskPhoneNumber(input) {
      if (!input || typeof input !== 'string') {
        return input || 'N/A';
      }

      const digits = input.replace(/\D/g, '');

      if (digits.length > 4) {
        return input.slice(0, 2) + input.slice(2, -2).replace(/\d/g, '*') + input.slice(-2);
      }

      return input;
    },

    showEmailForm() {
      this.emailError = '';
      if (!this.emailForm.recipientEmail && this.property.ownerEmail) {
        this.emailForm.recipientEmail = this.property.ownerEmail;
      }
      if (!this.emailForm.recipientPhone && this.property.ownerPhoneNo) {
        this.emailForm.recipientPhone = this.property.ownerPhoneNo;
      }
      this.emailMode = true;
    },

    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },

    validatePhone(phone) {
      // Basic phone validation - adjust regex as needed for your format
      const re = /^[0-9+\-\s()]{10,15}$/;
      return re.test(phone.replace(/\s/g, ''));
    },

    async sendEmailDemandNotice() {
      // Validation
      this.emailError = '';

      if (!this.emailForm.recipientEmail.trim()) {
        this.emailError = 'Email address is required';
        return;
      }

      if (!this.validateEmail(this.emailForm.recipientEmail)) {
        this.emailError = 'Please enter a valid email address';
        return;
      }

      if (this.emailForm.recipientPhone.trim() && !this.validatePhone(this.emailForm.recipientPhone.trim())) {
        this.emailError = 'Please enter a valid phone number or leave blank';
        return;
      }

      if (!this.property.id) {
        this.emailError = 'Property information is missing';
        return;
      }

      this.sendingEmail = true;

      try {
        const data = new FormData();
        data.append("function", "emailDemandNotice");
        data.append("propertyId", this.property.id);
        data.append("recipientEmail", this.emailForm.recipientEmail.trim());

        // Add phone number if provided
        if (this.emailForm.recipientPhone.trim()) {
          data.append("recipientPhone", this.emailForm.recipientPhone.trim());
        }

        const response = await execute(data, landRate);

        if (response.data.success) {
          this.emailSent = true;
          this.emailMode = false;

          // Show success message briefly
          this.$nextTick(() => {
            setTimeout(() => {
              // Auto-close modal after 5 seconds if user doesn't interact
              if (this.emailSent) {
                const modal = document.querySelector('#demandNotice');
                if (modal) {
                  const modalInstance = bootstrap.Modal.getInstance(modal);
                  if (modalInstance) {
                    modalInstance.hide();
                  }
                }
              }
            }, 5000);
          });

        } else {
          this.emailError = response.data.message || 'Failed to send notice';
        }
      } catch (error) {
        console.error('Sending error:', error);
        this.emailError = 'Error sending notice: ' + error.message;
      } finally {
        this.sendingEmail = false;
      }
    },

    resetEmailForm() {
      this.emailSent = false;
      this.emailMode = true;
      this.emailError = '';
      this.emailForm.recipientEmail = this.property.ownerEmail || '';
      this.emailForm.recipientPhone = this.property.ownerPhoneNo || '';
    }
  }
}
</script>

<style scoped>
.table th {
  font-weight: 600;
  font-size: 0.9rem;
}

.table td {
  font-size: 0.85rem;
}

.table-secondary {
  background-color: #6c757d !important;
  color: white;
}

.table-dark th {
  font-size: 1rem;
  font-weight: 700;
}

.alert-info {
  background-color: #e3f2fd;
  border-color: #2196f3;
  color: #0d47a1;
}

.alert-info h6 {
  color: #1565c0;
  margin-bottom: 0.5rem;
}

.alert-info ol {
  padding-left: 1.5rem;
}

.alert-info ol li {
  margin-bottom: 0.25rem;
}

.text-success { color: #28a745 !important; }
.text-info { color: #17a2b8 !important; }
.text-primary { color: #007bff !important; }
.text-warning { color: #ffc107 !important; }
.text-danger { color: #dc3545 !important; }

.modal-xl {
  max-width: 1200px;
}

.table-responsive {
  border-radius: 8px;
  overflow: hidden;
}

.avatar-lg {
  height: 4rem;
  width: 4rem;
}

.avatar-title {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
}

@media (max-width: 768px) {
  .modal-dialog {
    max-width: 95%;
    margin: 1rem auto;
  }

  .table td, .table th {
    font-size: 0.8rem;
    padding: 0.5rem;
  }
}
</style>